import { resolveAutoBypassTarget } from "@data/utility/familyUtils"
import { useEffect, useRef } from "react"
import { useSelector } from "react-redux"

/**
 * Shared auto-bypass resolver for deep-link flows.
 *
 * Reads pendingDeepLink / storedTokens / familyState from Redux, tracks the
 * URL already attempted via a ref (so a new incoming link gets a fresh pass),
 * and invokes onResolve(target) when resolveAutoBypassTarget returns a match.
 *
 * Screens own the side effect (token validation, press-handler dispatch,
 * loading UI). This hook centralizes the resolution and dedup logic that was
 * duplicated across AuthNavigator and the account picker (legacy
 * FamilyAccountSelectionScreen and its design-system port).
 *
 * Lives in data/ because it is redux + a pure resolver only: the ports layer
 * may import data/, never common/. The legacy callers import it from here too.
 */
export const useAutoBypassDeepLink = (onResolve, { enabled = true } = {}) => {
    const pendingDeepLink = useSelector((s) => s.notifications?.pendingDeepLink)
    const storedTokens = useSelector((s) => s.data?.authTokens?.storedTokens)
    const familyState = useSelector((s) => s.data?.family)
    const attemptedRef = useRef(null)
    const onResolveRef = useRef(onResolve)
    onResolveRef.current = onResolve

    useEffect(() => {
        if (!enabled) return
        if (!pendingDeepLink) return
        if (attemptedRef.current === pendingDeepLink) return

        const target = resolveAutoBypassTarget(
            pendingDeepLink,
            storedTokens,
            familyState
        )
        if (!target) return

        attemptedRef.current = pendingDeepLink
        onResolveRef.current(target)
    }, [enabled, pendingDeepLink, storedTokens, familyState])
}
